/**
 * Dashboard sidebar UI state — Benchmark Model, Service Provider, Telemetry
 * Connector.
 *
 * In the legacy Vue app these are Vuex `selectedModel` and `selectedCloudService`,
 * read by components well outside the sidebar (seven read the model, plus the
 * Cost Advice dispatcher), so they are shared state rather than one page's
 * private state — hence their own slice.
 *
 * Two deliberate divergences from legacy:
 *  - No persistence. Legacy writes `selectedCloudService` to localStorage from
 *    INSIDE the mutation, and separately serialises the whole store via
 *    vuex-persist. Storage is off-limits here (.claude/rules/security.md), and
 *    a reducer with a side effect is not something to port regardless.
 *  - The resets that follow a choice happen in the reducer, because they are a
 *    pure consequence of the same event. Legacy scatters that kind of reset
 *    across an action, a watcher and a route guard.
 *
 * Service Provider is ONE picker (cloud OR telemetry connector), as in legacy,
 * but stored as two fields so every consumer keeps reading a plain cloud:
 *  - selectedProvider — ALWAYS a cloud (AWS / AZURE / GCP / OCI). With a
 *    connector picked it is the cloud behind it (legacy's `datadogCSP`).
 *  - selectedTelemetry — the connector, or '' when a cloud was picked.
 * selectSelectedService gives back what the picker shows.
 */
import { createSlice } from '@reduxjs/toolkit';

import { BENCHMARK_MODELS, DEFAULT_BENCHMARK_MODEL } from './benchmarkModels';
import { DEFAULT_PORTFOLIO_SCOPE } from './portfolios';
import {
  BILLING_CONNECTOR,
  DEFAULT_CLOUD_PROVIDER,
  cloudsForTelemetry,
  isCloudProvider,
} from './serviceProviders';

const initialState = {
  selectedModel: DEFAULT_BENCHMARK_MODEL,
  selectedProvider: DEFAULT_CLOUD_PROVIDER,
  selectedTelemetry: '',
  portfolioScope: DEFAULT_PORTFOLIO_SCOPE,
  portfolioSearch: '',
  // Legacy Vuex `toggle`. Open by default; SidebarLayout closes it on small
  // screens and reopens it when the screen grows past the breakpoint.
  sidebarOpen: true,
  /**
   * The portfolio open on Manage Portfolio (legacy store `selectedPortfolio`):
   * { id, name, user, provider, model, billing? }, or null for a new, unsaved one. The
   * sidebar highlights it and locks Model / Service Provider to it.
   */
  openPortfolio: null,
  /**
   * A sidebar action the page must approve, because only the PAGE knows about
   * unsaved work and may first ask "discard unsaved changes?":
   *  - { kind: 'open', portfolio } — a portfolio row (or a notification);
   *  - { kind: 'new' }             — the ✕;
   *  - { kind: 'scope', scope }    — My / Org switch, after "Please Confirm";
   *  - { kind: 'service', change } — a Service Provider / Cloud pick; `change`
   *    is the action to dispatch once approved.
   * Any page that mounts the sidebar must handle it. Cleared once handled.
   */
  portfolioRequest: null,
  /**
   * Telemetry portfolios still being read by the server: portfolio id ->
   * 'pending' | 'in_progress' (legacy store telemetryJobStatusMap). The
   * sidebar shows a clock / spinner on those rows. Done = no entry. Keyed by
   * id — legacy keyed by name and matched rows by substring, so "abc" lit up
   * "abcd" too. Memory only, as legacy: a reload starts it empty.
   */
  telemetryJobStatus: {},
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setSelectedModel(state, action) {
      state.selectedModel = action.payload;
      // General IT Average has no telemetry column; a connector picked before
      // would be unreachable in the menu but still in effect — back to its cloud.
      if (action.payload === BENCHMARK_MODELS.GENERAL_IT_AVERAGE) {
        state.selectedTelemetry = '';
      }
    },
    /** A cloud picked in the Service Provider menu — replaces any connector. */
    setSelectedProvider(state, action) {
      state.selectedProvider = action.payload;
      state.selectedTelemetry = '';
      // Legacy clears the portfolio search on every Service Provider / Cloud
      // change (Cloud.vue:465, :491 — EventBus 'clearSearch').
      state.portfolioSearch = '';
    },
    /**
     * A connector picked in the Service Provider menu. Its cloud becomes the
     * provider: the only one it has, or the first of its choices — legacy
     * defaults the `Cloud*` field the same way (Cloud.vue:510-512).
     */
    setSelectedTelemetry(state, action) {
      const connector = action.payload;
      state.selectedTelemetry = connector;
      const clouds = cloudsForTelemetry(connector);
      if (clouds.length > 0) state.selectedProvider = clouds[0];
      state.portfolioSearch = '';
    },
    /** The extra "Cloud" field under Datadog / Prometheus / Billing. */
    setTelemetryCloud(state, action) {
      if (cloudsForTelemetry(state.selectedTelemetry).includes(action.payload)) {
        state.selectedProvider = action.payload;
        state.portfolioSearch = '';
      }
    },
    setPortfolioScope(state, action) {
      state.portfolioScope = action.payload;
      // Legacy blanks the search box on every scope change (handleSwitch calls
      // clearSearch first). The two scopes list different portfolios, so a
      // stale query would read as "no results" rather than "different list".
      state.portfolioSearch = '';
    },
    setPortfolioSearch(state, action) {
      state.portfolioSearch = action.payload;
    },
    /**
     * { id, status }: 'pending' / 'in_progress' is stored; anything else
     * (completed, complete, failed, empty) removes the mark — legacy's rule.
     */
    setTelemetryJobStatus(state, action) {
      const { id, status } = action.payload ?? {};
      if (!id) return;
      const mark = String(status ?? '').toLowerCase();
      if (mark === 'pending' || mark === 'in_progress') state.telemetryJobStatus[id] = mark;
      else delete state.telemetryJobStatus[id];
    },
    setSidebarOpen(state, action) {
      state.sidebarOpen = Boolean(action.payload);
    },
    /** The sidebar asks the page to open a portfolio, or start a new one. */
    requestPortfolio(state, action) {
      state.portfolioRequest = action.payload;
    },
    clearPortfolioRequest(state) {
      state.portfolioRequest = null;
    },
    /**
     * The page opened a portfolio (or closed it: null). Model and Service
     * Provider follow it, as legacy's Cloud.vue watcher does — model 'scaler'
     * is General IT Average, anything else SPEC CPU; the provider only when it
     * is a cloud (a connector portfolio keeps the current pick).
     *
     * A Billing portfolio (`billing: true`, see portfolios.js toOpenPortfolio)
     * is saved under its cloud (legacy sends `datadogCSP` as the provider), so
     * that cloud becomes Billing's "Cloud" pick and Billing STAYS picked — the
     * page must remain in Billing (CUR) mode for it, as legacy's does.
     */
    setOpenPortfolio(state, action) {
      const portfolio = action.payload;
      state.openPortfolio = portfolio;
      if (!portfolio) return;
      state.selectedModel =
        String(portfolio.model ?? '').toLowerCase() === 'scaler'
          ? BENCHMARK_MODELS.GENERAL_IT_AVERAGE
          : BENCHMARK_MODELS.SPEC_CPU;
      const provider = String(portfolio.provider ?? '').toUpperCase();
      if (portfolio.billing) {
        state.selectedTelemetry = BILLING_CONNECTOR;
        if (cloudsForTelemetry(BILLING_CONNECTOR).includes(provider)) {
          state.selectedProvider = provider;
        }
      } else if (isCloudProvider(provider)) {
        state.selectedProvider = provider;
        state.selectedTelemetry = '';
      }
    },
  },
});

export const {
  setSelectedModel,
  setSelectedProvider,
  setSelectedTelemetry,
  setTelemetryCloud,
  setPortfolioScope,
  setPortfolioSearch,
  setSidebarOpen,
  requestPortfolio,
  clearPortfolioRequest,
  setOpenPortfolio,
  setTelemetryJobStatus,
} = dashboardSlice.actions;

export const selectSelectedModel = (s) => s.dashboard?.selectedModel ?? DEFAULT_BENCHMARK_MODEL;
export const selectSelectedProvider = (s) =>
  s.dashboard?.selectedProvider ?? DEFAULT_CLOUD_PROVIDER;
export const selectSelectedTelemetry = (s) => s.dashboard?.selectedTelemetry ?? '';
/** What the Service Provider picker shows: the connector if one is picked, else the cloud. */
export const selectSelectedService = (s) => selectSelectedTelemetry(s) || selectSelectedProvider(s);
export const selectPortfolioScope = (s) => s.dashboard?.portfolioScope ?? DEFAULT_PORTFOLIO_SCOPE;
export const selectPortfolioSearch = (s) => s.dashboard?.portfolioSearch ?? '';
export const selectSidebarOpen = (s) => s.dashboard?.sidebarOpen ?? true;
const NO_JOBS = Object.freeze({});
/** Portfolio id -> 'pending' | 'in_progress', for telemetry portfolios still being read. */
export const selectTelemetryJobStatus = (s) => s.dashboard?.telemetryJobStatus ?? NO_JOBS;
export const selectOpenPortfolio = (s) => s.dashboard?.openPortfolio ?? null;
export const selectPortfolioRequest = (s) => s.dashboard?.portfolioRequest ?? null;

export default dashboardSlice.reducer;
